import React, { useEffect, useState } from "react";

//Material UI
import {
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { MobileDateTimePicker } from "@mui/x-date-pickers/MobileDateTimePicker";

//Helpers
import { typographyForText } from "../../Helpers/typography";

export default function Form({ data, handleStateChange }) {
  // ==========> Component States <==========
  const [compState, setCompState] = useState(generateState());

  // ==========> Component Functions <==========
  /**
   * Function to generate the states for each element in the form
   * @returns the new state of each form's item
   */
  function generateState() {
    const initState = {};
    for (const { id, initialValue } of data) {
      initState[id] = initialValue;
    }
    return initState;
  }

  /**
   * UseEffect for changes in state
   */
  useEffect(() => {
    handleStateChange(compState);
  }, [compState]);

  // ==========> Component useEffect <==========

  //Function to select the component (textfield or selector).
  const selectTypeOfInput = (obj) => {
    switch (obj.selection) {
      case "textfield":
        return generateTextField(obj);
      case "selector":
        return generateSelector(obj);
      case "date_picker":
        return generateDatePicker(obj);
      case "checkbox":
        return generateCheckbox(obj);
      default:
        return null;
    }
  };

  //Function to generate the texfield component.
  const generateTextField = (obj) => {
    return (
      <Grid container>
        <Grid item xs={12} sx={{ padding: "10px" }}>
          {typographyForText(obj.label, "bold")}
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            placeholder={obj.placeholder}
            sx={obj.style}
            value={compState[obj.id]}
            multiline={obj.multiline}
            type={obj.type}
            InputProps={obj.inputProps}
            inputProps={obj.inputProps}
            onChange={(e) => {
              const newState = { ...compState };
              newState[obj.id] = e.target.value;
              setCompState(newState);
            }}
          />
        </Grid>
      </Grid>
    );
  };

  //Function to generate the date picker component
  const generateDatePicker = (obj) => {
    return (
      <Grid container>
        <Grid item xs={12} sx={{ padding: "10px" }}>
          {typographyForText(obj.label, "bold")}
        </Grid>
        <Grid item xs={12}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <MobileDateTimePicker
              inputFormat="dd-MM-yyyy HH:mm"
              value={compState[obj.id]}
              onChange={(e) => {
                const newState = { ...compState };
                newState[obj.id] = e;
                setCompState(newState);
              }}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>
    );
  };

  //Function to generate the selector component
  const generateSelector = (obj) => {
    const generateContent = () => {
      return obj.content.map((element, index) => {
        return (
          <MenuItem key={`selection-${index}-${obj.label}`} value={index}>
            {element}
          </MenuItem>
        );
      });
    };
    return (
      <FormControl sx={{ width: "100%" }}>
        <Grid container justifyContent="center">
          <Grid item xs={12} sx={{ padding: "10px" }}>
            {typographyForText(obj.label, "bold")}
          </Grid>
          <Grid item xs={12}>
            <Select
              sx={obj.style}
              labelId={obj.label}
              id={obj.label}
              value={compState[obj.id]}
              onChange={(e) => {
                const newState = { ...compState };
                newState[obj.id] = e.target.value;
                setCompState(newState);
              }}
            >
              {generateContent()}
            </Select>
          </Grid>
        </Grid>
      </FormControl>
    );
  };

  //Function to generate the checkbox component
  const generateCheckbox = (obj) => {
    return (
      <Grid container justifyContent="flex-start" textAlign="left">
        <Grid item xs={12} sx={{ padding: "10px" }}>
          <FormControlLabel
            control={
              <Switch
                checked={compState[obj.id] || false}
                onChange={(e) => {
                  const newState = { ...compState };
                  newState[obj.id] = e.target.checked;
                  setCompState(newState);
                }}
                color="secondary"
              />
            }
            label={obj.label}
          />
        </Grid>
      </Grid>
    );
  };

  /**
   * Function to generate the form items
   * @returns the items of the entire form (textfield and selectors)
   */
  const generateFormItems = () => {
    let items = [];
    for (const element of data) {
      items.push(
        <Grid item key={`${element.id}`} xs={element.xs} md={element.md}>
          {selectTypeOfInput(element)}
        </Grid>
      );
    }
    return items;
  };

  // ==========> Component <==========
  return (
    <Grid
      container
      alignItems="flex-end"
      justifyContent="center"
      spacing={1}
      sx={{ padding: "10px" }}
    >
      {generateFormItems()}
    </Grid>
  );
}
